import { useState, useEffect } from "react";
import apiFetch from "../../../../shared/services/apiClient";

/**
 * useSaldoFavor — destino y rubro del sobrante de un recibo.
 *
 * Lo comparten Generar Recibo y Aplicar Recibo: en las dos pantallas, lo que no
 * se aplique a ninguna factura queda como crédito de la filial, y las dos tienen
 * que mandar exactamente el mismo cuerpo a `acumular_saldo`.
 *
 * El destino va acá y no en el check de la tabla de CxC porque ahí el check
 * significa lo contrario: GASTAR el crédito de esa factura en las demás. Por eso
 * las facturas marcadas se excluyen de la lista de destinos.
 *
 * @param {Array} facturasData — las CxC de la filial, tal como las arma useCxc.
 */
export function useSaldoFavor(facturasData) {
  const [destinoFavor, setDestinoFavor] = useState("nueva");
  const [rubroFavor, setRubroFavor]     = useState("");
  const [rubros, setRubros]             = useState([]);

  // El mismo catálogo de cuentas de ingreso que usan los saldos iniciales. Solo
  // hace falta cuando el crédito arranca una CxC nueva: acumulado sobre una
  // factura que ya existe, hereda el rubro de esa factura.
  useEffect(() => {
    apiFetch("/api/finance/saldo-inicial/rubros/")
      .then((r) => r.json())
      .then((d) => setRubros(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const destinosFavor = (facturasData || []).filter(
    (f) => Number(f.saldo) < 0 && !f.checked && f.factura?.id_factura != null,
  );

  // Si el destino elegido desaparece de la lista (se marcó para gastar su
  // crédito, o cambió la filial), volver a la factura de apoyo: mandarle al
  // backend un id que ya no aplica lo haría acumular sobre la factura equivocada.
  useEffect(() => {
    if (destinoFavor === "nueva") return;
    const sigue = (facturasData || []).some(
      (f) =>
        Number(f.saldo) < 0 &&
        !f.checked &&
        String(f.factura?.id_factura) === String(destinoFavor),
    );
    if (!sigue) setDestinoFavor("nueva");
  }, [facturasData, destinoFavor]);

  /**
   * Cuerpo del POST a `acumular_saldo`. Lanza si falta el rubro, que es
   * obligatorio solo cuando hay que crear la factura de apoyo.
   */
  function cuerpoAcumular(fechaPago) {
    const body = { fecha_pago: fechaPago };
    if (destinoFavor === "nueva") {
      if (!rubroFavor) {
        throw new Error(
          "Elegí el rubro con el que queda registrado el saldo a favor.",
        );
      }
      body.rubro = Number(rubroFavor);
    } else {
      body.target_factura = Number(destinoFavor);
    }
    return body;
  }

  return {
    destinoFavor,
    setDestinoFavor,
    rubroFavor,
    setRubroFavor,
    rubros,
    destinosFavor,
    cuerpoAcumular,
  };
}
